import { Alert, Button, Stack, Typography } from '@mui/material';
import {
  BenefitActivationDTO,
  BenefitDTOBenefitEnum,
  BenefitTemplateItemDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getExtensionForBenefit } from '../../module/benefits';
import { ActivationDialogExtensions } from '../../module/benefits/ActivationDialog';
import { useBenefitContext } from '../../module/benefits/BenefitContext';
import { SelectedBikeleasingCondition } from '../../module/benefits/bikeleasingV2/SelectedBikeleasingCondition';
import { useBikeleasingCondition } from '../../module/benefits/bikeleasingV2/useBikeleasingCondition';
import { BenefitActivationDialogExtensionInterface } from '../interface/benefit-activation-dialog.extension.interface';
interface BikeLeasingBenefitOptions {}

const Fields: React.FC<{ showHint?: boolean }> = ({ showHint = false }) => {
  const { data: bikeleasingCondition } = useBikeleasingCondition();
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();
  const benefitActivationDialogExtension = getExtensionForBenefit(benefit, BenefitActivationDialogExtensionInterface);

  return (
    <>
      {!bikeleasingCondition && (
        <Stack sx={{ marginBottom: 2 }}>
          <Alert
            severity="warning"
            action={
              <Button
                component={Link}
                to={`/benefits/${benefit.toLowerCase()}`}
                sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
                variant="text"
                color="inherit"
                size="small"
              >
                {t('extensions:bikeleasing_v2_benefit.conditionDetailDialog.alertAction')}
              </Button>
            }
          >
            {t('extensions:bikeleasing_v2_benefit.conditionDetailDialog.alert')}
          </Alert>
        </Stack>
      )}
      <SelectedBikeleasingCondition bikeleasingCondition={bikeleasingCondition} />
      {!!benefitActivationDialogExtension?.createActivationHintKey && showHint && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t(benefitActivationDialogExtension.createActivationHintKey)}
        </Typography>
      )}
    </>
  );
};

const ChangeOptionsInfo: React.FC = () => {
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase();

  return (
    <Typography color="text.secondary" mb={2}>
      <Trans
        i18nKey="extensions:bikeleasing_v2.activationDialog.changeOptionsInfo"
        components={{ settings: <Link to={`/benefits/${benefit}/settings`} /> }}
      />
    </Typography>
  );
};

const resetBikeleasingBenefitActivationOptions: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO,
): BikeLeasingBenefitOptions => {
  return {};
};

const createBikeleasingBenefitActivation: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  bikeleasingOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.BikeleasingV2,
  };
  await saveActivation(create);
};

const updateBikeleasingBenefitActivation: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  _bikeleasingOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
  };
  await saveActivation(update);
};

export class BikeleasingBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<BikeLeasingBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields showHint />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  titleKey? = 'extensions:bikeleasing_v2.activationDialog.createTitle';
  bulkTitleKey? = 'extensions:bikeleasing_v2.activationDialog.bulkCreateTitle';
  ChangeOptionsInfo?: React.ComponentType = ChangeOptionsInfo;
  deactivateActivationHintKey?: string = 'extensions:bikeleasing_v2.activationDialog.deactivationHint';
  createActivationHintKey?: string = 'extensions:bikeleasing_v2.activationDialog.activationHint';
  hideSaveButton: () => boolean = (): boolean => true;

  onCreateActivation = createBikeleasingBenefitActivation;
  onUpdateActivation = updateBikeleasingBenefitActivation;
  onResetBenefitOptions = resetBikeleasingBenefitActivationOptions;
}
